import { Component, inject } from '@angular/core';
import { Router, RouterOutlet } from '@angular/router';
import { MessageService } from 'primeng/api';
import { Toast } from 'primeng/toast';
import { ButtonModule } from 'primeng/button';
import { TranslocoDirective } from '@jsverse/transloco';
import { InquiryEventsService } from './core/services/inquiry-events.service';
import { AnalyticsService } from './core/services/analytics.service';
import { CapyStatsService } from './core/services/capystats.service';
import { ConsentBannerComponent } from './shared/components/consent-banner/consent-banner.component';

// Layout-less public routes MUST stay cookieless and lightweight: no SSE,
// no analytics, no consent banner.
// - /embed/ renders inside third-party iframes.
// - /gosc/ and /guest/ carry a secret guest-info token in the URL path and
//   must never be reported to analytics (auto-pageviews would log the token)
//   nor open an SSE stream.
// Checked via location (not Router) so the eager injects below can be skipped
// before the first navigation resolves.
//
// WARNING (token-leak ban): this check runs ONCE at document load. GA4/Meta/GTM
// additionally filter these paths per-navigation in AnalyticsService, but
// CapyStats auto-pageview tracking lives entirely in the external /a.js script
// and CANNOT be filtered app-side. Therefore in-app router links navigating to
// /gosc/... or /guest/... MUST NEVER be added while CapyStats auto-tracking is
// on — once the tracker is injected on a regular page, such a navigation would
// report the secret-token URL.
const IS_LAYOUTLESS_ROUTE =
  typeof location !== 'undefined' && /^\/(embed|gosc|guest)\//.test(location.pathname);

@Component({
  selector: 'app-root',
  imports: [RouterOutlet, Toast, ButtonModule, TranslocoDirective, ConsentBannerComponent],
  templateUrl: './app.html',
  styleUrl: './app.scss'
})
export class App {
  private readonly router = inject(Router);
  private readonly messageService = inject(MessageService);
  protected readonly isLayoutlessRoute = IS_LAYOUTLESS_ROUTE;
  // Eagerly initialize SSE listener so it works on all pages,
  // not just pages that include HeaderComponent.
  private readonly _ = IS_LAYOUTLESS_ROUTE ? null : inject(InquiryEventsService);
  // Eagerly initialize analytics so its consent/branding effect runs app-wide
  // (white-label only, gated behind cookie consent). Inert on the main host.
  private readonly _analytics = IS_LAYOUTLESS_ROUTE ? null : inject(AnalyticsService);
  // Eagerly initialize CapyStats (cookieless): main-host key on zaklepuj.pl
  // (DNT/GPC only), per-domain key on white-label hosts (consent-gated).
  private readonly _capyStats = IS_LAYOUTLESS_ROUTE ? null : inject(CapyStatsService);

  /**
   * Chat toast „Reply”. The guest token is NOT put into the URL (trackers read
   * location.href): the toast comes from a thread stored in
   * InquiryThreadsService, where the chat page looks the token up by threadId.
   */
  openThread(threadId: string): void {
    this.messageService.clear('chat');
    this.router.navigate(['/inquiry/reply'], { queryParams: { threadId } });
  }
}
